import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { getAllBookings } from './adminService';
import { createMapsUrl } from './components/location/locationUtils';
import {
  Calendar,
  Clock,
  Search,
  Filter,
  CheckCircle,
  XCircle,
  AlertCircle,
  User,
  GraduationCap,
  DollarSign,
  BookOpen,
  MapPin,
  Video,
  ChevronDown,
  ChevronUp,
  ExternalLink
} from 'lucide-react';

export default function AdminBookingsPage() {
  const { isArabic } = useOutletContext();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedBooking, setExpandedBooking] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Load bookings on mount and when filters change
  useEffect(() => {
    loadBookings();
  }, [statusFilter]);

  // Load bookings with filters
  async function loadBookings() {
    try {
      setLoading(true);
      setError(null);

      const filters = {
        search: searchQuery,
        limit: 100
      };

      if (statusFilter !== 'all') {
        filters.status = statusFilter;
      }

      if (dateFrom) {
        filters.date_from = dateFrom;
      }

      if (dateTo) {
        filters.date_to = dateTo;
      }

      const data = await getAllBookings(filters);
      setBookings(data.bookings || []);
    } catch (err) {
      console.error('Error loading bookings:', err);
      setError(err.message || 'Failed to load bookings');
    } finally {
      setLoading(false);
    }
  }

  // Handle search
  function handleSearch() {
    loadBookings();
  }

  // Format currency
  function formatCurrency(amount) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'BHD'
    }).format(amount || 0);
  }

  // Get status badge
  function getStatusBadge(status) {
    const statusConfig = {
      pending: {
        label: isArabic ? 'قيد الانتظار' : 'Pending',
        bg: '#fff3e0',
        color: '#FF9800',
        icon: Clock
      },
      accepted: {
        label: isArabic ? 'مقبول' : 'Accepted',
        bg: '#e8f5e9',
        color: '#4CAF50',
        icon: CheckCircle
      },
      rejected: {
        label: isArabic ? 'مرفوض' : 'Rejected',
        bg: '#ffebee',
        color: '#f44336',
        icon: XCircle
      },
      completed: {
        label: isArabic ? 'مكتمل' : 'Completed',
        bg: '#e3f2fd',
        color: '#2196F3',
        icon: CheckCircle
      }
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <span style={{
        padding: '6px 12px',
        background: config.bg,
        color: config.color,
        borderRadius: 12,
        fontSize: 13,
        fontWeight: 600,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6
      }}>
        <Icon size={14} />
        {config.label}
      </span>
    );
  }

  // Status filter options
  const statusOptions = [
    { value: 'all', label: isArabic ? 'الكل' : 'All' },
    { value: 'pending', label: isArabic ? 'قيد الانتظار' : 'Pending' },
    { value: 'accepted', label: isArabic ? 'مقبول' : 'Accepted' },
    { value: 'rejected', label: isArabic ? 'مرفوض' : 'Rejected' },
    { value: 'completed', label: isArabic ? 'مكتمل' : 'Completed' }
  ];

  // Calculate stats
  const stats = {
    total: bookings.length,
    pending: bookings.filter(b => b.status === 'pending').length,
    accepted: bookings.filter(b => b.status === 'accepted').length,
    rejected: bookings.filter(b => b.status === 'rejected').length,
    totalRevenue: bookings
      .filter(b => b.status === 'accepted' || b.status === 'completed')
      .reduce((sum, b) => sum + (b.total_price || 0), 0)
  };

  return (
    <div style={{ maxWidth: 1400, margin: '0 auto' }}>
      {/* Page Header */}
      <div style={{ marginBottom: 32 }}>
        <h1 style={{
          fontSize: 32,
          fontWeight: 700,
          color: '#232B3E',
          margin: '0 0 8px 0'
        }}>
          {isArabic ? 'إدارة الحجوزات' : 'Booking Management'}
        </h1>
        <p style={{ color: '#666', margin: 0, fontSize: 16 }}>
          {isArabic
            ? 'مراقبة وإدارة جميع الحجوزات على المنصة'
            : 'Monitor and manage all bookings on the platform'}
        </p>
      </div>

      {/* Stats Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: 16,
        marginBottom: 24
      }}>
        <StatCard
          label={isArabic ? 'إجمالي الحجوزات' : 'Total Bookings'}
          value={stats.total}
          color="#2196F3"
        />
        <StatCard
          label={isArabic ? 'قيد الانتظار' : 'Pending'}
          value={stats.pending}
          color="#FF9800"
        />
        <StatCard
          label={isArabic ? 'مقبول' : 'Accepted'}
          value={stats.accepted}
          color="#4CAF50"
        />
        <StatCard
          label={isArabic ? 'مرفوض' : 'Rejected'}
          value={stats.rejected}
          color="#f44336"
        />
        <StatCard
          label={isArabic ? 'الإيرادات المتوقعة' : 'Expected Revenue'}
          value={formatCurrency(stats.totalRevenue)}
          color="#FF7426"
        />
      </div>

      {/* Search and Filter Bar */}
      <div style={{
        background: '#fff',
        borderRadius: 12,
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        padding: 20,
        marginBottom: 24
      }}>
        <div style={{ display: 'flex', gap: 12, marginBottom: showFilters ? 16 : 0 }}>
          <div style={{ flex: 1, position: 'relative' }}>
            <Search
              size={20}
              style={{
                position: 'absolute',
                left: isArabic ? 'auto' : 12,
                right: isArabic ? 12 : 'auto',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#999'
              }}
            />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              placeholder={isArabic ? 'البحث بالطالب أو المعلم...' : 'Search by student or teacher...'}
              style={{
                width: '100%',
                padding: '12px 12px 12px 40px',
                paddingLeft: isArabic ? 12 : 40,
                paddingRight: isArabic ? 40 : 12,
                border: '1px solid #e0e0e0',
                borderRadius: 8,
                fontSize: 14,
                outline: 'none'
              }}
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{
              padding: '12px 16px',
              border: '1px solid #e0e0e0',
              borderRadius: 8,
              fontSize: 14,
              cursor: 'pointer',
              background: '#fff',
              minWidth: 150
            }}
          >
            {statusOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <button
            onClick={() => setShowFilters(!showFilters)}
            style={{
              padding: '12px 20px',
              background: showFilters ? '#FF7426' : '#fff',
              color: showFilters ? '#fff' : '#232B3E',
              border: '1px solid #e0e0e0',
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}
          >
            <Filter size={16} />
            {isArabic ? 'تصفية' : 'Filter'}
          </button>

          <button
            onClick={handleSearch}
            disabled={loading}
            style={{
              padding: '12px 24px',
              background: '#FF7426',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 600,
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1,
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}
          >
            <Search size={16} />
            {isArabic ? 'بحث' : 'Search'}
          </button>
        </div>

        {/* Advanced Filters */}
        {showFilters && (
          <div style={{
            paddingTop: 16,
            borderTop: '1px solid #e0e0e0',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 12
          }}>
            <div>
              <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 6 }}>
                {isArabic ? 'من تاريخ' : 'Date From'}
              </label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #e0e0e0',
                  borderRadius: 6,
                  fontSize: 14
                }}
              />
            </div>
            <div>
              <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 6 }}>
                {isArabic ? 'إلى تاريخ' : 'Date To'}
              </label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #e0e0e0',
                  borderRadius: 6,
                  fontSize: 14
                }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div style={{
          background: '#fee',
          border: '1px solid #fcc',
          borderRadius: 8,
          padding: 16,
          marginBottom: 24,
          display: 'flex',
          alignItems: 'center',
          gap: 12
        }}>
          <AlertCircle size={20} color="#c00" />
          <p style={{ color: '#c00', margin: 0 }}>{error}</p>
        </div>
      )}

      {/* Bookings List */}
      <div style={{
        background: '#fff',
        borderRadius: 12,
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        overflow: 'hidden'
      }}>
        {loading ? (
          <div style={{ padding: 60, textAlign: 'center' }}>
            <div style={{
              width: 48,
              height: 48,
              border: '4px solid #e0e0e0',
              borderTop: '4px solid #FF7426',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 16px'
            }}></div>
            <p style={{ color: '#666' }}>
              {isArabic ? 'جاري التحميل...' : 'Loading...'}
            </p>
          </div>
        ) : bookings.length === 0 ? (
          <div style={{ padding: 60, textAlign: 'center' }}>
            <Calendar size={48} color="#ccc" style={{ margin: '0 auto 16px' }} />
            <p style={{ color: '#666', fontSize: 16 }}>
              {isArabic ? 'لا توجد حجوزات' : 'No bookings found'}
            </p>
          </div>
        ) : (
          <div>
            {bookings.map((booking) => (
              <BookingCard
                key={booking.id}
                booking={booking}
                isArabic={isArabic}
                isExpanded={expandedBooking === booking.id}
                onToggleExpand={() => setExpandedBooking(expandedBooking === booking.id ? null : booking.id)}
                getStatusBadge={getStatusBadge}
                formatCurrency={formatCurrency}
              />
            ))}
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

// StatCard Component
function StatCard({ label, value, color }) {
  return (
    <div style={{
      background: '#fff',
      borderRadius: 12,
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      padding: 16,
      borderLeft: `4px solid ${color}`
    }}>
      <p style={{ color: '#666', margin: '0 0 8px 0', fontSize: 13 }}>{label}</p>
      <h3 style={{ fontSize: 24, fontWeight: 700, color: '#232B3E', margin: 0 }}>
        {value}
      </h3>
    </div>
  );
}

// BookingCard Component
function BookingCard({ booking, isArabic, isExpanded, onToggleExpand, getStatusBadge, formatCurrency }) {
  return (
    <div style={{
      borderBottom: '1px solid #f0f0f0',
      padding: 20,
      background: isExpanded ? '#f7f9fb' : '#fff',
      transition: 'background 0.2s'
    }}>
      {/* Header Row */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: isExpanded ? 16 : 0,
        cursor: 'pointer'
      }}
      onClick={onToggleExpand}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, color: '#232B3E', margin: 0 }}>
              {booking.subject}
            </h3>
            {getStatusBadge(booking.status)}
          </div>

          <div style={{ display: 'flex', gap: 24, fontSize: 14, color: '#666' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <User size={14} />
              <span>{booking.student_name}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <GraduationCap size={14} />
              <span>{booking.teacher_name}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Calendar size={14} />
              <span>{new Date(booking.date).toLocaleDateString()}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Clock size={14} />
              <span>{booking.time_from} - {booking.time_to}</span>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ textAlign: 'right' }}>
            <p style={{ fontSize: 12, color: '#666', margin: '0 0 4px 0' }}>
              {isArabic ? 'السعر' : 'Price'}
            </p>
            <p style={{ fontSize: 18, fontWeight: 700, color: '#FF7426', margin: 0 }}>
              {formatCurrency(booking.total_price)}
            </p>
          </div>
          {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </div>
      </div>

      {/* Expanded Details */}
      {isExpanded && (
        <div style={{
          paddingTop: 16,
          borderTop: '1px solid #e0e0e0',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 16
        }}>
          <DetailItem
            icon={<BookOpen size={16} />}
            label={isArabic ? 'المادة' : 'Subject'}
            value={booking.subject}
          />
          <DetailItem
            icon={<MapPin size={16} />}
            label={isArabic ? 'نوع الدرس' : 'Lesson Format'}
            value={booking.lesson_format === 'online' ? (isArabic ? 'أونلاين' : 'Online') : (isArabic ? 'حضوري' : 'In-person')}
          />
          {booking.lesson_format === 'online' ? (
            <DetailItem
              icon={<Video size={16} />}
              label={isArabic ? 'رابط الاجتماع' : 'Meeting Link'}
              value={booking.meeting_link || (isArabic ? 'لم يتم الإنشاء بعد' : 'Not created yet')}
            />
          ) : (
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <MapPin size={16} style={{ color: '#64748b' }} />
                <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>
                  {isArabic ? 'الموقع' : 'Location'}
                </span>
              </div>
              <div style={{ fontSize: 14, color: '#1e293b', marginLeft: 24 }}>
                {booking.location_formatted_address || booking.place || (isArabic ? 'غير محدد' : 'N/A')}
                {booking.location_latitude && booking.location_longitude && (
                  <a
                    href={createMapsUrl(booking.location_latitude, booking.location_longitude)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      marginLeft: 8,
                      color: '#3b82f6',
                      textDecoration: 'none',
                      fontSize: 13,
                      fontWeight: 500
                    }}
                  >
                    <ExternalLink size={14} />
                    {isArabic ? 'عرض في الخريطة' : 'View on Map'}
                  </a>
                )}
              </div>
            </div>
          )}
          <DetailItem
            icon={<DollarSign size={16} />}
            label={isArabic ? 'السعر بالساعة' : 'Hourly Rate'}
            value={formatCurrency(booking.hourly_rate)}
          />
          <DetailItem
            icon={<Clock size={16} />}
            label={isArabic ? 'المدة' : 'Duration'}
            value={`${booking.duration_hours} ${isArabic ? 'ساعة' : 'hours'}`}
          />
          <DetailItem
            icon={<Calendar size={16} />}
            label={isArabic ? 'تاريخ الإنشاء' : 'Created At'}
            value={new Date(booking.created_at).toLocaleString()}
          />
        </div>
      )}
    </div>
  );
}

// DetailItem Component
function DetailItem({ icon, label, value }) {
  return (
    <div>
      <p style={{
        fontSize: 12,
        color: '#999',
        margin: '0 0 6px 0',
        display: 'flex',
        alignItems: 'center',
        gap: 6
      }}>
        {icon}
        {label}
      </p>
      <p style={{ fontSize: 14, color: '#232B3E', margin: 0, fontWeight: 500 }}>
        {value}
      </p>
    </div>
  );
}
